import React, { useRef } from 'react'
import { OmTooltip, OmCheckbox } from '@owma/kit'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import styles from './style.module.scss'
import Avatar from '../Avatar'

import privateIcon from 'assets/images/calendar/private-16.svg'
import recurringIcon from 'assets/images/calendar/recurrence-16.svg'
import exceptionIcon from 'assets/images/calendar/exception-16.svg'
import * as dateUtil from 'pages/calendar/utils/DateUtils'
import alarmIcon from 'assets/images/calendar/reminder-icon.svg'

import { getReminderTimeStr } from 'pages/todo/components/DateFormatter'
import { updateTaskStatus } from 'pages/todo/modules/TodoManager'

interface Attendee {
  val: string
  params: {
    CN?: string
    ROLE?: string
    PARTSTAT?: string
  }
}

interface TodoItem {
  summary: string
  status: string
  alarms?: any[]
  dueDate?: Date
  startDate?: Date
}

interface CalendarEvent {
  id: string
  startDate: Date
  endDate: Date
  subject: string
  location?: string
  color?: string
  allday?: boolean
  attendees?: Attendee[]
  isPrivate?: boolean
  isRecurring?: boolean
  hasException?: boolean
  todo?: TodoItem
}

interface AgendaCellProps {
  event: CalendarEvent
  date: Date
  onEventClick?: (event: CalendarEvent, date: Date, ref?: React.RefObject<HTMLDivElement>) => void
}

const AgendaCell: React.FC<AgendaCellProps> = ({ event, date, onEventClick }) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)

  const formatEventTime = (startTime: Date, endTime: Date, isAllDay: boolean): string => {
    if (isAllDay) {
      return t('calendar.edit.allDay')
    }
    return `${dateUtil.localizedTimeString(startTime)} → ${dateUtil.localizedTimeString(endTime)}`
  }

  const renderAttendees = (event: CalendarEvent): React.ReactNode => {
    const attendees = event.attendees
    if (!attendees?.length) return null

    const maxDisplay = 6
    const remaining = attendees.length <= maxDisplay ? 0 : attendees.length - maxDisplay + 1
    const displayAttendees =
      remaining === 0 ? attendees.slice() : attendees.slice(0, maxDisplay - 1)

    return (
      <div className={styles.attendees}>
        {displayAttendees.map((attendee) => {
          const title = attendee.params.CN ? attendee.params.CN : null
          return (
            <OmTooltip
              key={attendee.val}
              title={
                <React.Fragment>
                  {title}
                  {title ? <br /> : null}
                  {attendee.val}
                </React.Fragment>
              }
              placement="bottom"
            >
              <div className={styles.avatar}>
                <Avatar name={attendee.params.CN || attendee.val} email={attendee.val} />
              </div>
            </OmTooltip>
          )
        })}
        {remaining > 0 && <div className={styles.more}>+{remaining}</div>}
      </div>
    )
  }

  const renderEventIcons = (event: CalendarEvent): React.ReactNode => {
    if (!event.isPrivate && !event.isRecurring && !event.hasException) return null

    return (
      <div className={styles.eventIcons}>
        {event.isPrivate && <img src={privateIcon} alt="private" />}
        {event.isRecurring && !event.hasException && <img src={recurringIcon} alt="recurring" />}
        {event.hasException && <img src={exceptionIcon} alt="exception" />}
      </div>
    )
  }

  const onCompleteToggle = (isChecked: boolean): void => {
    console.log('onCompleteToggle:', event.todo?.summary, isChecked)
    if (event.todo) {
      ;(updateTaskStatus as any)(event.todo, isChecked)
    }
  }

  const renderContent = (): React.ReactNode => {
    if (event.todo) {
      return (
        <div className={styles.todo}>
          <div className={styles.checkBoxArea} onClick={(e) => e.stopPropagation()}>
            <OmCheckbox
              checked={event.todo.status === 'COMPLETED'}
              onChange={(isChecked) => {
                onCompleteToggle(isChecked)
              }}
              className={styles.checkBox}
              style={{ '--checkbox-color': event.color || '#000' } as React.CSSProperties}
            />
          </div>
          <div className={styles.infoArea}>
            <div
              className={classNames(
                styles.summary,
                event.todo.status === 'COMPLETED' ? styles.completed : '',
              )}
            >
              {event.subject}
            </div>
            {event.todo.alarms && event.todo.alarms.length > 0 && (
              <div className={styles.alarm}>
                <img src={alarmIcon} alt="alarm" className={styles.icon} />
                {getReminderTimeStr(
                  event.todo.alarms[0],
                  event.todo.dueDate,
                  event.todo.startDate,
                  t,
                )}
              </div>
            )}
            {renderEventIcons(event)}
          </div>
        </div>
      )
    }
    return (
      <>
        <div className={styles.time}>
          {formatEventTime(event.startDate, event.endDate, event.allday || false)}
        </div>
        <div className={styles.textWrapper}>
          <div className={styles.title}>{event.subject}</div>
          <div className={styles.location}>{event.location}</div>
        </div>
        {renderAttendees(event)}
        {renderEventIcons(event)}
      </>
    )
  }

  return (
    <div
      className={styles.eventItem}
      style={{ '--belt-color': event.color || '#000' } as React.CSSProperties}
      onClick={() => onEventClick?.(event, date, ref)}
      ref={ref}
    >
      {renderContent()}
    </div>
  )
}

export default AgendaCell
